import { useState, useEffect, useCallback } from 'react';
import { useElectron } from './useElectron';
import { NetworkMode } from '../types/electron';

interface UseNetworkModeOptions {
  /** User ID for offline profile management */
  userId?: string;
}

/**
 * Hook for managing network mode (online/offline)
 * @param options - Options including userId for offline profile management
 */
export function useNetworkMode(options: UseNetworkModeOptions = {}) {
  const { userId } = options;
  const { network } = useElectron();
  // NOTE: By default, we can assume 'online' mode until we load the actual mode from Electron API. This prevents unnecessary "offline" flashes on app start.
  const [networkMode, setNetworkMode] = useState<NetworkMode>('online');
  const [isLoading, setIsLoading] = useState(false);

  // Load current network mode on mount
  useEffect(() => {
    const loadNetworkMode = async () => {
      if (!network) {
        setNetworkMode('offline');
        return;
      }

      try {
        const result = await network.getMode();
        if (result.success && result.mode) {
          setNetworkMode(result.mode);
        }
      } catch (error) {
        console.error('Error loading network mode:', error);
        setNetworkMode('offline');
      }
    };

    loadNetworkMode();
  }, [network]);

  // Switch network mode
  const switchMode = useCallback(
    async (mode: NetworkMode) => {
      if (!network) {
        console.warn('Electron API not available');
        return false;
      }

      if (!userId && mode === 'offline') {
        console.warn('No userId provided for offline mode - profile may not be saved correctly');
      }

      setIsLoading(true);

      try {
        const result = await network.setModeWithUser(mode, userId || 'unknown');

        if (result.success) {
          setNetworkMode(mode);
          // Window will reload automatically after mode change
          return true;
        }

        return false;
      } catch (error) {
        console.error('Error switching network mode:', error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [network, userId]
  );

  // Toggle between online and offline
  const toggleMode = useCallback(() => {
    const newMode: NetworkMode = networkMode === 'online' ? 'offline' : 'online';
    return switchMode(newMode);
  }, [networkMode, switchMode]);

  return {
    networkMode,
    isOnline: networkMode === 'online',
    isOffline: networkMode === 'offline',
    isLoading,
    switchMode,
    toggleMode,
  };
}
